import React, { JSX, ReactNode, useMemo, useRef, useState } from 'react'
import { ChatOverlayMockup } from 'components/overlayPreview/OverlayMockups'
import { usePublicHomePreviewSeam } from 'components/overlayPreview/usePreviewSeam'
import { usePrefersReducedMotion } from 'components/overlayPreview/previewFeeds'
import { CHAT_FONT_FAMILIES } from 'lib/chatFontCatalog'
import { PUBLIC_HOME_COPY as COPY } from 'lib/publicHomeCopy'
import { useNearViewport } from 'components/publicHome/publicHomeHooks'
import {
  DEMO_FONT_KEYS,
  DEMO_SEED,
  DEMO_SHADOWS,
  DemoFontKey,
  DemoShadow,
  SAMPLE_BADGES_ON,
  demoChatConfig
} from 'components/publicHome/publicHomeSamples'

export const HOME_FRAME_CLASSES = 'w-full max-w-full overflow-hidden rounded-2xl border border-home-line bg-home-frame shadow-[0_2px_10px_1px_#0003]'

const PILL_FOCUS_RING = 'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brand'

const PILL_CLASSES = 'inline-flex min-h-11 cursor-pointer items-center justify-center rounded-full border px-4 text-sm font-medium transition-colors duration-150'

export const LEGEND_CLASSES = 'text-xs font-medium uppercase tracking-wider text-dm-text-accent'

const CAPTION_CLASSES = 'mt-3 text-xs leading-[18px] text-dm-text-accent sm:text-[13px] sm:leading-5'

export const RadioPill = <T extends string>({
  name,
  value,
  checked,
  onSelect,
  children
}: {
  name: string
  value: T
  checked: boolean
  onSelect: (value: T) => void
  children: ReactNode
}): JSX.Element => {
  const state = checked
    ? 'border-white/40 bg-white/15 text-dm-text-primary'
    : 'border-white/15 text-dm-text-secondary hover:border-white/30 hover:text-dm-text-primary'
  return (
    <label className={`${PILL_CLASSES} ${state} ${PILL_FOCUS_RING}`}>
      <input
        type='radio'
        className='sr-only'
        name={name}
        value={value}
        checked={checked}
        onChange={() => onSelect(value)}
      />
      {children}
    </label>
  )
}

const SHADOW_LABELS: Readonly<Record<DemoShadow, string>> = {
  OFF: COPY.chat.shadowOff,
  SOFT: COPY.chat.shadowSoft,
  HARD: COPY.chat.shadowHard
}

export const ChatOverlayDemo = (): JSX.Element => {
  const seam = usePublicHomePreviewSeam(DEMO_SEED)
  const stageRef = useRef<HTMLDivElement | null>(null)
  const near = useNearViewport(stageRef)
  const reducedMotion = usePrefersReducedMotion(true)
  const [fontKey, setFontKey] = useState<DemoFontKey>(DEMO_FONT_KEYS[0])
  const [shadow, setShadow] = useState<DemoShadow>('SOFT')
  const [badges, setBadges] = useState(SAMPLE_BADGES_ON)
  const config = useMemo(() => demoChatConfig({ fontKey, shadow, badges }), [fontKey, shadow, badges])

  return (
    <figure className='w-full max-w-full'>
      <div className={HOME_FRAME_CLASSES}>
        <div className='border-b border-white/10 px-4 py-2 text-xs font-medium uppercase tracking-wider text-dm-text-accent'>
          {COPY.chat.demoTitle}
        </div>

        <div
          ref={stageRef}
          aria-hidden='true'
          className='h-80 overflow-hidden bg-gradient-to-br from-zinc-700 via-zinc-800 to-zinc-600 px-3 sm:h-[380px] [&_[data-testid=overlay-sample-tag]]:hidden'
        >
          {near && <ChatOverlayMockup seam={seam} config={config} fillHost />}
        </div>
        <div className='flex flex-col gap-4 border-t border-white/10 p-4'>
          <fieldset>
            <legend className={LEGEND_CLASSES}>{COPY.chat.fontLabel}</legend>
            <div className='mt-2 flex flex-wrap gap-2'>
              {DEMO_FONT_KEYS.map((key) => (
                <RadioPill
                  key={key}
                  name='public-home-demo-font'
                  value={key}
                  checked={fontKey === key}
                  onSelect={setFontKey}
                >
                  {CHAT_FONT_FAMILIES[key].family}
                </RadioPill>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend className={LEGEND_CLASSES}>{COPY.chat.shadowLabel}</legend>
            <div className='mt-2 flex flex-wrap gap-2'>
              {DEMO_SHADOWS.map((value) => (
                <RadioPill
                  key={value}
                  name='public-home-demo-shadow'
                  value={value}
                  checked={shadow === value}
                  onSelect={setShadow}
                >
                  {SHADOW_LABELS[value]}
                </RadioPill>
              ))}
            </div>
          </fieldset>

          {SAMPLE_BADGES_ON && (
            <label className={`inline-flex min-h-11 cursor-pointer items-center gap-3 self-start rounded-full pr-2 ${PILL_FOCUS_RING}`}>
              <input
                type='checkbox'
                role='switch'
                className='sr-only'
                checked={badges}
                onChange={(e) => setBadges(e.target.checked)}
              />
              <span className={LEGEND_CLASSES}>{COPY.chat.badgesLabel}</span>
              <span
                aria-hidden='true'
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-150 ${badges ? 'bg-brand' : 'bg-white/15'}`}
              >
                <span className={`inline-block size-4 rounded-full bg-white shadow transition-transform duration-150 motion-reduce:transition-none ${badges ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
              </span>
              <span aria-hidden='true' className='text-sm text-dm-text-primary'>
                {badges ? COPY.chat.on : COPY.chat.off}
              </span>
            </label>
          )}
        </div>
      </div>
      <figcaption className={CAPTION_CLASSES}>{COPY.chat.demoCaption}</figcaption>
      {reducedMotion && <p className={CAPTION_CLASSES}>{COPY.reducedMotionChat}</p>}
    </figure>
  )
}
